import type { VehicleOverviewSearchResult } from '@gm-commercial/rtk-clients/assets/types';
import {
  type AssetConnectivityStatus,
  type AssetModel,
  type AssetServiceStatus,
  BatteryRange,
  DiagnosticSeverity,
  DiagnosticState,
  type LocationMaskingStatus,
  type OnstarConnectionStatus,
  type OnstarSupportStatus,
  type OrganizationOwnershipStatus,
  type ProvisioningStatus,
  type VehicleConnectionStatus,
} from '~/common/models/asset.model';
import {
  type AdvisorVehicleListItem,
  type ApiDiagnostic,
  type AssetReportItem,
  BatteryChargingStatus,
  type ChargerType,
  type Diagnostic,
  DiagnosticDTCType,
  isTwelveVoltBatterySacType,
  type LastCommunicated,
  type PowerType,
  ReadyStatus,
  ReportDiagnosticState,
  type TiresStatus,
  type VehicleDetailsType,
  VehiclePropulsion,
} from '~/common/models/asset-report.model';
import type { Entity } from '~/common/models/common.model';
import type { ApiRecallsItem } from '~/common/models/recalls.model';
import type { OnStarUnitState } from '~/common/models/vehicle.model';
import {
  MAX_VALID_SOC_VALUE,
  MIN_VALID_SOC_VALUE,
} from '~/common/utils/battery.utils';
import {
  pickMostSevereTwelveVoltSac,
  resolveTwelveVoltSacType,
} from '~/common/utils/diagnostics.utils';
import {
  isLocationMaskingEnabled,
  mapApiResponseToType,
  mapConnectivityStatusType,
} from '../../mappers/asset.mappers';

export type ApiReadinessItem = Entity & {
  type?: { model: string; version: string };
  organization?: Partial<Entity>;
  hub?: Partial<Entity>;
  fleet?: Partial<Entity>;
  ready?: ReadyStatus;
  faultCount?: number;
  battery?: Partial<{
    state: ReportDiagnosticState;
    soc: number;
    chargeState: BatteryChargingStatus;
    estimatedRangedInKM: number;
  }>;
  tires?: ReportDiagnosticState;
  brakeFluid?: ReportDiagnosticState;
  washerFluid?: ReportDiagnosticState;
  connectivity?:
    | {
        onStar?: {
          connectionStatus?: OnstarConnectionStatus | null;
        };
      }
    | OnstarConnectionStatus
    | null;
  onstarSupportStatus?: OnstarSupportStatus | null;
  connectionStatus?: VehicleConnectionStatus | null;
  lastUpdated?: string;
  safetyCompliant?: boolean;
  milState?: ReportDiagnosticState;
  diagnosticState?: ReportDiagnosticState | null;
  availabilityState?: AssetServiceStatus;
  fuelLevel?: Partial<{
    state: ReportDiagnosticState;
    fuelLevel: number;
  }>;
  licensePlate?: string;
  oilLife?: Partial<{
    state: ReportDiagnosticState;
    oilLife: number;
  }>;
  brakePadFront?: Partial<{
    state: ReportDiagnosticState;
    brakePadLife: number;
  }>;
  brakePadRear?: Partial<{
    state: ReportDiagnosticState;
    brakePadLife: number;
  }>;
  engineAirFilter?: Partial<{
    state: ReportDiagnosticState;
    engineAirFilter: number;
  }>;
  vehicleType?: VehicleDetailsType;
  lastCommunicated?:
    | Partial<{
        status: LastCommunicated;
        deviceTimestamp: string;
      }>
    | string
    | null;
  provisioning?: ProvisioningStatus | null;
  organizationPrimaryOwnershipStatus?: OrganizationOwnershipStatus | null;
  tcpsAccepted?: boolean | null;
  diagnostics?: ApiDiagnostic[];
  diagnosticsCritical?: boolean | null;
  diagnosticsImpaired?: boolean | null;
  /** Prognostics are reported separately from DTCs; absent until any SAC code is sent. */
  prognostics?:
    | ApiDiagnostic[]
    | Record<string, Omit<ApiDiagnostic, 'type'> & { name?: string }>;
  prognosticsCritical?: boolean | null;
  organizationId?: string;
  fleetId?: string | null;
  lastOnboardedDate?: string | null;
  vehicleImageSource?: string | null;
  onStarUnitState?: OnStarUnitState | null;
  locationMasking?: LocationMaskingStatus | null;
  userAction?: VehicleOverviewSearchResult['userAction'] | null;
};

export type ApiTiresReadinessItem = Entity & {
  type: { model: string; version: string };
  organization?: Partial<Entity>;
  hub?: Partial<Entity>;
  fleet?: Partial<Entity>;
  ready?: ReadyStatus;
  tires?: {
    leftFront: TiresStatus;
    leftRear: TiresStatus;
    rightFront: TiresStatus;
    rightRear: TiresStatus;
  };
  connectivity?: AssetConnectivityStatus;
  lastUpdated?: string;
  availabilityState?: AssetServiceStatus;
  licensePlate?: string;
  vehicleType?: VehicleDetailsType;
  lastCommunicated?: Partial<{
    status: LastCommunicated;
    deviceTimestamp: string;
  }>;
};

export type ApiBatteryReadinessItem = Entity & {
  type: { model: string; version: string };
  organization?: Partial<Entity>;
  hub?: Partial<Entity>;
  fleet?: Partial<Entity>;
  connectivity?: {
    onStar?: {
      connectionStatus?: OnstarConnectionStatus | null;
    };
  } | null;
  onstarSupportStatus?: OnstarSupportStatus | null;
  connectionStatus?: VehicleConnectionStatus | null;
  lastUpdated?: string;
  battery?: Partial<{
    state: ReportDiagnosticState;
    soc: number;
    targetSoc: number;
    chargeState: BatteryChargingStatus;
    estimatedRangedInKM: number;
    /** @deprecated from v 0.7 - use chargingPowerType instead */
    chargePowerType: PowerType;
    estimatedTimeRemainingToTargetSOC: number;
    lastUpdated: string;
    /** @deprecated from v 0.7 - use chargeCompletionTime instead */
    chargingEstimatedCompletionDateTime: string;
    chargingPowerType: ChargerType;
    chargeCompletionTime: string;
  }>;
  licensePlate?: string;
  vehicleType?: VehicleDetailsType;
  lastCommunicated?: Partial<{
    status: LastCommunicated;
    deviceTimestamp: string;
  }>;
  provisioning?: ProvisioningStatus | null;
  organizationPrimaryOwnershipStatus?: OrganizationOwnershipStatus | null;
  tcpsAccepted?: boolean | null;
};

export const BATTERY_RANGE_MAPPING: {
  [k in ReportDiagnosticState]?: BatteryRange;
} = {
  [ReportDiagnosticState.GOOD]: BatteryRange.HIGH,
  [ReportDiagnosticState.IMPAIRED]: BatteryRange.MEDIUM,
  [ReportDiagnosticState.CRITICAL]: BatteryRange.LOW,
};

export const DIAGNOSTIC_STATE_MAPPING: {
  [k in ReportDiagnosticState]?: DiagnosticState;
} = {
  [ReportDiagnosticState.GOOD]: DiagnosticState.CLEAR,
  [ReportDiagnosticState.IMPAIRED]: DiagnosticState.IMPAIRED,
  [ReportDiagnosticState.CRITICAL]: DiagnosticState.CRITICAL,
};

export const DIAGNOSTIC_SEVERITY_MAPPING: {
  [k in ReportDiagnosticState]?: DiagnosticSeverity;
} = {
  [ReportDiagnosticState.GOOD]: DiagnosticSeverity.INFO,
  [ReportDiagnosticState.IMPAIRED]: DiagnosticSeverity.IMPAIRED,
  [ReportDiagnosticState.CRITICAL]: DiagnosticSeverity.CRITICAL,
  [ReportDiagnosticState.NO_DATA]: DiagnosticSeverity.UNKNOWN,
};

export enum VehicleImageSizes {
  SIZE_205 = 'gmds3.png',
  SIZE_320 = 'gmds5.png',
  SIZE_480 = 'gmds1.png',
  SIZE_640 = 'gmds4.png',
  SIZE_960 = 'gmds2.png',
  SIZE_1920 = 'gmds10.png',
}

export enum VehicleImageAngles {
  FRONT_01 = 'deg01',
  FRONT_02 = 'deg02',
  FRONT_06 = 'deg06',
  SIDE_03 = 'deg03',
  BACK_04 = 'deg04',
  BACK_05 = 'deg05',
}

export enum VehicleMakes {
  CADILLAC = 'Cadillac',
  GMC = 'GMC',
  CHEVY = 'Chevy',
  BUICK = 'Buick',
}

export function buildVehicleImageURL(
  vehicleType: VehicleDetailsType | undefined,
  size = VehicleImageSizes.SIZE_320,
  angle = VehicleImageAngles.FRONT_01
) {
  const {
    merchandisingModel = '',
    year = '',
    peg = '',
    rpoCodes,
  } = vehicleType ?? {};
  const necessaryItems = [merchandisingModel, peg, year].filter(Boolean);

  if (
    !necessaryItems.length ||
    necessaryItems.length < 3 ||
    !rpoCodes?.length
  ) {
    return '';
  }

  const BASE = 'https://cgi.chevrolet.com/mmgprod-us/dynres/prove/image.gen';
  const PARAMS = `${size}&v=${angle}&std=true&transparentBackgroundPng=true&country=US`;

  return `${BASE}?i=${year}/${merchandisingModel}/${merchandisingModel}__${peg}/${rpoCodes?.join('_')}${PARAMS}`;
}

export const getVehicleAngle = (make: string) => {
  let angle = VehicleImageAngles.FRONT_01;
  if (!make) {
    return angle;
  }

  // suggestion from the CGI team that cadillac is the only one currently using deg02
  if (make.toLowerCase() === VehicleMakes.CADILLAC.valueOf().toLowerCase()) {
    angle = VehicleImageAngles.FRONT_02;
  }
  return angle;
};

export const mapOverviewApiVehicleTypeToDetails = (
  apiVehicleType?: VehicleOverviewSearchResult | null
): VehicleDetailsType | undefined => {
  if (apiVehicleType == null) return undefined;
  const p = apiVehicleType?.propulsion;
  const propulsion =
    p != null &&
    Object.values(VehiclePropulsion).includes(p as VehiclePropulsion)
      ? (p as VehiclePropulsion)
      : undefined;
  return {
    make: apiVehicleType.make,
    year: apiVehicleType.year,
    model: apiVehicleType.model,
    propulsion,
  };
};

/**
 * @param item - Raw item from overview API (VehicleOverviewSearchResult)
 * @returns AssetReportItem with vehicleType set to a VehicleDetailsType object
 */
export const mapVehicleListOverviewItemToAssetReportItem = ({
  item,
  isNewConnectivityStatusFeatureFlag = false,
}: {
  item: VehicleOverviewSearchResult;
  isNewConnectivityStatusFeatureFlag?: boolean;
}): AssetReportItem => {
  const {
    id = '',
    name = '',
    onStarUnitState,
    connectionStatus,
    ...apiResponse
  } = item;
  const vehicleType = mapOverviewApiVehicleTypeToDetails(item);

  return {
    id,
    name,
    type: { model: '', version: '' },
    onStarUnitState: onStarUnitState as OnStarUnitState | null | undefined,
    licensePlate: apiResponse.licensePlate ?? undefined,
    connectivityBoardingStatus: mapConnectivityStatusType({
      organizationOwnershipStatus:
        apiResponse?.organizationPrimaryOwnershipStatus as
          | OrganizationOwnershipStatus
          | undefined,
      onStarConnectionStatus:
        typeof apiResponse?.connectivity === 'string'
          ? (apiResponse.connectivity as OnstarConnectionStatus)
          : undefined,
      connectionStatus:
        (connectionStatus as VehicleConnectionStatus | undefined) ?? undefined,
      onstarSupportStatus: apiResponse?.onStarSupportStatus as
        | OnstarSupportStatus
        | undefined,
      isNotEquippedFeatureFlag: true, // NOT_EQUIPPED_FLAG flag to control the mapping of VERIFIED to NOT_EQUIPPED.
      isNewConnectivityStatusFeatureFlag,
    }),
    vehicleType,
    imageUrl: apiResponse.vehicleImageSource ?? '',
    lastOnboardedDate: apiResponse.lastOnboardedDate ?? null,
    lastIgnition: apiResponse.lastIgnition ?? undefined,
    fleet: apiResponse.fleetId
      ? {
          id: apiResponse.fleetId,
          name: apiResponse.fleetName ?? '',
        }
      : undefined,
    organization: apiResponse.organizationId
      ? {
          id: apiResponse.organizationId,
          name: '',
        }
      : undefined,
    locationMasking: isLocationMaskingEnabled(
      apiResponse.locationMaskingStatus as LocationMaskingStatus | null
    ),
    userAction: apiResponse.userAction,
  };
};

/**
 * Maps VehicleListOverview API response to AssetReportItem for the consolidated vehicle list.
 * Used by RTK Query integration for the Overview tab when MARCH_VEHICLE_MANAGEMENT_FLAG is enabled
 * TODO: SDPCPLT-2418 Update this function when vehicleListView is fully refactored and integrated with RTK Query.
 * Acceptance criteria: All vehicle list API calls use generated RTK Query hooks instead of thunk actions.
 */
export const mapApiVehicleListOverviewItemConsolidated = (
  item: Partial<ApiReadinessItem>,
  isNotEquippedFeatureFlag: boolean,
  isNewConnectivityStatusFeatureFlag = false
): AssetReportItem => {
  const {
    id = '',
    name = '',
    vehicleType,
    onStarUnitState,
    ...apiResponse
  } = item;
  const type = apiResponse?.type
    ? mapApiResponseToType(apiResponse?.type)
    : { model: '', version: '' };
  return {
    id,
    name,
    type,
    isTcpsAccepted: apiResponse?.tcpsAccepted,
    onStarUnitState,
    licensePlate: apiResponse.licensePlate ?? undefined,
    connectivityBoardingStatus: mapConnectivityStatusType({
      organizationOwnershipStatus:
        apiResponse?.organizationPrimaryOwnershipStatus,
      onStarConnectionStatus:
        typeof apiResponse?.connectivity === 'object' &&
        apiResponse?.connectivity !== null
          ? apiResponse.connectivity.onStar?.connectionStatus
          : typeof apiResponse?.connectivity === 'string'
            ? (apiResponse.connectivity as OnstarConnectionStatus)
            : undefined,
      connectionStatus: apiResponse?.connectionStatus ?? undefined,
      onstarSupportStatus: apiResponse?.onstarSupportStatus,
      isNotEquippedFeatureFlag,
      isNewConnectivityStatusFeatureFlag,
    }),
    vehicleType,
    lastCommunicated: mapLastCommunicated(apiResponse.lastCommunicated),
    imageUrl: apiResponse.vehicleImageSource ?? '',
    lastOnboardedDate: apiResponse.lastOnboardedDate ?? null,
    fleet: apiResponse.fleetId
      ? {
          id: apiResponse.fleetId,
          name: '',
        }
      : undefined,
    organization: apiResponse.organizationId
      ? {
          id: apiResponse.organizationId,
          name: '',
        }
      : undefined,
    locationMasking: isLocationMaskingEnabled(apiResponse.locationMasking),
  };
};

/**
 * Legacy readiness `/readinessReport` row shape. Used for notification/peripheral
 * endpoints and test fixtures that still receive the pre-consolidation API payload.
 */
export const mapLegacyApiAssetReadinessToReportItem = (
  item: Partial<ApiReadinessItem>,
  isNotEquippedFeatureFlag?: boolean,
  isNewConnectivityStatusFeatureFlag = false
): AssetReportItem => {
  const {
    id,
    name,
    organization,
    fleet,
    faultCount,
    vehicleType,
    ...apiResponse
  } = item;
  const type = apiResponse?.type
    ? mapApiResponseToType(apiResponse?.type)
    : undefined;
  const angle = getVehicleAngle(vehicleType?.make ?? '');

  return {
    id,
    name,
    organization,
    fleet,
    type,
    faultCount,
    ready: mapReadiness(apiResponse.ready),
    battery: {
      isCharging:
        apiResponse.battery?.chargeState === BatteryChargingStatus.CHARGING
          ? true
          : apiResponse.battery?.chargeState === BatteryChargingStatus.UNPLUGGED
            ? false
            : undefined,
      range: apiResponse.battery?.state
        ? BATTERY_RANGE_MAPPING[apiResponse.battery?.state] ||
          BatteryRange.UNKNOWN
        : undefined,
      value: apiResponse.battery?.soc ?? undefined,
      estimatedRange:
        typeof apiResponse.battery?.estimatedRangedInKM === 'number'
          ? {
              value: apiResponse.battery?.estimatedRangedInKM,
              unitOfMeasure: 'KM',
            }
          : undefined,
    },
    connectivity: apiResponse.connectivity,
    brakeFluid: apiResponse.brakeFluid,
    washerFluid: apiResponse.washerFluid,
    tires: apiResponse.tires,
    estimatedRange:
      typeof apiResponse.battery?.estimatedRangedInKM === 'number'
        ? {
            value: apiResponse.battery?.estimatedRangedInKM,
            unitOfMeasure: 'KM',
          }
        : undefined,
    safetyCompliant: apiResponse.safetyCompliant,
    lastUpdated: apiResponse.lastUpdated,
    milState: apiResponse.milState,
    diagnosticHealth: apiResponse.diagnosticState,
    availability: apiResponse.availabilityState,
    fuel: {
      state: apiResponse.fuelLevel?.state ?? undefined,
      value: apiResponse.fuelLevel?.fuelLevel ?? undefined,
    },
    licensePlate: apiResponse.licensePlate ?? undefined,
    oilLife: {
      state: apiResponse.oilLife?.state ?? undefined,
      value: apiResponse.oilLife?.oilLife ?? undefined,
    },
    brakePadFront: {
      state: apiResponse.brakePadFront?.state ?? undefined,
      value: apiResponse.brakePadFront?.brakePadLife ?? undefined,
    },
    brakePadRear: {
      state: apiResponse.brakePadRear?.state ?? undefined,
      value: apiResponse.brakePadRear?.brakePadLife ?? undefined,
    },
    engineAirFilter: {
      state: apiResponse.engineAirFilter?.state ?? undefined,
      value: apiResponse.engineAirFilter?.engineAirFilter ?? undefined,
    },
    connectivityBoardingStatus: mapConnectivityStatusType({
      organizationOwnershipStatus:
        apiResponse?.organizationPrimaryOwnershipStatus,
      onStarConnectionStatus:
        typeof apiResponse?.connectivity === 'object' &&
        apiResponse?.connectivity !== null
          ? apiResponse.connectivity.onStar?.connectionStatus
          : undefined,
      connectionStatus: apiResponse?.connectionStatus ?? undefined,
      onstarSupportStatus: apiResponse?.onstarSupportStatus,
      isNotEquippedFeatureFlag: isNotEquippedFeatureFlag ?? false,
      isNewConnectivityStatusFeatureFlag,
    }),
    vehicleType,
    lastCommunicated: mapLastCommunicated(apiResponse.lastCommunicated),
    // This path never threads the 12-volt flag, so `mapDiagnostics` would strip any prognostic
    // it was given. Claiming `prognosticsCritical` here would leave the cell with nothing to show.
    diagnostics: mapDiagnostics(apiResponse.diagnostics),
    imageUrl:
      apiResponse.vehicleImageSource ??
      buildVehicleImageURL(item.vehicleType, undefined, angle),
    diagnosticsCritical: apiResponse?.diagnosticsCritical ?? null,
    diagnosticsImpaired: apiResponse?.diagnosticsImpaired ?? null,
  } as AssetReportItem;
};

/** Vehicle overview list / consolidated readiness row (default list path). */
export const mapApiAssetReadinessToReportItem = (
  item: Partial<ApiReadinessItem>,
  isNotEquippedFeatureFlag?: boolean,
  isNewConnectivityStatusFeatureFlag = false
): AssetReportItem =>
  mapApiVehicleListOverviewItemConsolidated(
    item,
    isNotEquippedFeatureFlag ?? false,
    isNewConnectivityStatusFeatureFlag
  );

/**
 * Maps diagnostics list API response to AssetReportItem
 * This is a dedicated mapper for the vehicle diagnostics list that handles
 * the specific response format from the /vehicleDiagnosticsList endpoint.
 * @param item - API response item from diagnostics list
 * @param isTwelveVoltBatteryEnabled - Whether 12-volt battery prognostics are surfaced
 * @returns Mapped AssetReportItem for diagnostics table
 */
export const mapApiAssetDiagnosticsToReportItem = (
  item: Partial<ApiReadinessItem>,
  isTwelveVoltBatteryEnabled = false
): AssetReportItem => {
  // Extract core fields using actual API keys from the diagnostics endpoint
  const {
    id,
    name,
    organizationId,
    fleetId,
    vehicleType,
    licensePlate,
    vehicleImageSource,
    availabilityState,
    lastOnboardedDate,
    lastCommunicated,
    diagnosticsCritical,
    diagnosticsImpaired,
    diagnostics,
    prognostics,
    prognosticsCritical,
    battery,
    fuelLevel,
    oilLife,
  } = item;

  const angle = getVehicleAngle(vehicleType?.make ?? '');

  return {
    id,
    name,
    // Map organizationId/fleetId to Entity structure expected by AssetReportItem
    organization: organizationId ? { id: organizationId } : undefined,
    fleet: fleetId ? { id: fleetId } : undefined,
    // Type is required by AssetReportItem but not in diagnostics API response
    type: { model: 'unknown', version: 'unknown' },
    // Vehicle identification
    licensePlate: licensePlate ?? undefined,
    vehicleType,
    imageUrl:
      vehicleImageSource ?? buildVehicleImageURL(vehicleType, undefined, angle),
    // Availability
    availability: availabilityState,
    lastOnboardedDate: lastOnboardedDate ?? undefined,
    // Last communicated
    lastCommunicated: mapLastCommunicated(lastCommunicated),
    // Diagnostics - critical fields for diagnostics view
    // Prognostics are reported in their own array; both feed the same collapse.
    diagnostics: mapDiagnostics(
      [...(diagnostics ?? []), ...toPrognosticList(prognostics)],
      isTwelveVoltBatteryEnabled
    ),
    diagnosticsCritical: diagnosticsCritical ?? null,
    diagnosticsImpaired: diagnosticsImpaired ?? null,
    // mapDiagnostics strips 12-volt entries when the flag is off, so the summary follows suit.
    prognosticsCritical: isTwelveVoltBatteryEnabled
      ? (prognosticsCritical ?? null)
      : null,
    // Fluids & components that exist in API response
    fuel: {
      state: fuelLevel?.state ?? undefined,
      value: fuelLevel?.fuelLevel ?? undefined,
    },
    oilLife: {
      state: oilLife?.state ?? undefined,
      value: oilLife?.oilLife ?? undefined,
    },
    // Battery info
    battery: {
      isCharging:
        battery?.chargeState === BatteryChargingStatus.CHARGING
          ? true
          : battery?.chargeState === BatteryChargingStatus.UNPLUGGED
            ? false
            : undefined,
      range: battery?.state
        ? BATTERY_RANGE_MAPPING[battery?.state] || BatteryRange.UNKNOWN
        : undefined,
      value: battery?.soc ?? undefined,
      estimatedRange:
        typeof battery?.estimatedRangedInKM === 'number'
          ? {
              value: battery?.estimatedRangedInKM,
              unitOfMeasure: 'KM',
            }
          : undefined,
    },
  } as AssetReportItem;
};

export const mapApiAssetTireReadinessToReportItem = ({
  id,
  name,
  organization,
  hub,
  fleet,
  vehicleType,
  ...apiResponse
}: Partial<ApiTiresReadinessItem>): AssetReportItem => {
  const type = apiResponse?.type
    ? mapApiResponseToType(apiResponse?.type)
    : undefined;
  return {
    id,
    name,
    organization,
    hub,
    fleet,
    type,
    ready: mapReadiness(apiResponse.ready),
    leftRearTire: apiResponse?.tires?.leftRear,
    leftFrontTire: apiResponse?.tires?.leftFront,
    rightFrontTire: apiResponse?.tires?.rightFront,
    rightRearTire: apiResponse?.tires?.rightRear,
    connectivity: apiResponse.connectivity,
    lastUpdated: apiResponse.lastUpdated,
    availability: apiResponse.availabilityState,
    licensePlate: apiResponse.licensePlate ?? undefined,
    vehicleType,
    lastCommunicated: {
      deviceTimestamp:
        apiResponse.lastCommunicated?.deviceTimestamp ?? undefined,
      status: apiResponse.lastCommunicated?.status ?? undefined,
    },
  } as AssetReportItem;
};

export const mapApiAssetBatteryReadinessToReportItem = (
  {
    id,
    name,
    organization,
    hub,
    fleet,
    vehicleType,
    ...apiResponse
  }: Partial<ApiBatteryReadinessItem>,
  isNotEquippedFeatureFlag = false,
  isNewConnectivityStatusFeatureFlag = false
): AssetReportItem => {
  const type = apiResponse?.type
    ? mapApiResponseToType(apiResponse?.type)
    : undefined;

  const angle = getVehicleAngle(vehicleType?.make ?? '');

  return {
    id,
    name,
    organization,
    hub,
    fleet,
    type,
    connectivity: apiResponse.connectivity,
    lastUpdated: apiResponse.lastUpdated,
    battery: {
      isCharging:
        apiResponse.battery?.chargeState === BatteryChargingStatus.CHARGING
          ? true
          : apiResponse.battery?.chargeState === BatteryChargingStatus.UNPLUGGED
            ? false
            : undefined,
      range: apiResponse.battery?.state
        ? BATTERY_RANGE_MAPPING[apiResponse.battery?.state] ||
          BatteryRange.UNKNOWN
        : undefined,
      value: apiResponse.battery?.soc ?? undefined,
      estimatedRange:
        typeof apiResponse.battery?.estimatedRangedInKM === 'number'
          ? {
              value: apiResponse.battery?.estimatedRangedInKM,
              unitOfMeasure: 'KM',
            }
          : undefined,
      lastUpdated: apiResponse.battery?.lastUpdated,
      chargingPowerType: apiResponse.battery?.chargingPowerType || undefined,
      chargeCompletionTime:
        apiResponse.battery?.chargeCompletionTime || undefined,
    },
    targetSOC: {
      value: apiResponse.battery?.targetSoc
        ? apiResponse.battery.targetSoc
        : undefined,
      estimatedRange:
        typeof apiResponse.battery?.estimatedRangedInKM === 'number' &&
        apiResponse.battery?.targetSoc &&
        apiResponse.battery?.soc &&
        apiResponse.battery?.soc >= MIN_VALID_SOC_VALUE &&
        apiResponse.battery?.soc <= MAX_VALID_SOC_VALUE
          ? {
              value: calculateTargetSocEstRange(
                apiResponse.battery?.soc,
                apiResponse.battery?.estimatedRangedInKM,
                apiResponse.battery?.targetSoc
              ),
              unitOfMeasure: 'KM',
            }
          : undefined,
    },
    chargingStatus: apiResponse.battery?.chargeState,
    powerType: apiResponse.battery?.chargePowerType,
    minutesToTargetSOC: {
      value: apiResponse.battery?.estimatedTimeRemainingToTargetSOC,
      deviceTimestamp: apiResponse.battery?.chargingEstimatedCompletionDateTime,
    },
    licensePlate: apiResponse.licensePlate ?? undefined,
    vehicleType,
    lastCommunicated: {
      deviceTimestamp:
        apiResponse.lastCommunicated?.deviceTimestamp ?? undefined,
      status: apiResponse.lastCommunicated?.status ?? undefined,
    },
    imageUrl: buildVehicleImageURL(vehicleType, undefined, angle),
    connectivityBoardingStatus: mapConnectivityStatusType({
      organizationOwnershipStatus:
        apiResponse?.organizationPrimaryOwnershipStatus,
      onStarConnectionStatus:
        apiResponse?.connectivity?.onStar?.connectionStatus,
      connectionStatus: apiResponse?.connectionStatus ?? undefined,
      onstarSupportStatus: apiResponse?.onstarSupportStatus,
      isNotEquippedFeatureFlag,
      isNewConnectivityStatusFeatureFlag,
    }),
  } as AssetReportItem;
};
export const mapApiRecallsToReportItem = ({
  id,
  name,
  organization,
  hub,
  fleet,
  vehicleType,
  ...apiResponse
}: Partial<ApiRecallsItem>): AssetReportItem => {
  const type = apiResponse?.type
    ? mapApiResponseToType(apiResponse?.type)
    : undefined;

  const angle = getVehicleAngle(vehicleType?.make ?? '');

  return {
    id,
    name,
    organization,
    hub,
    fleet,
    type,
    availability: apiResponse.availabilityState,
    licensePlate: apiResponse.licensePlate ?? undefined,
    vehicleType,
    fieldActionSummary: apiResponse.fieldActionSummary,
    imageUrl: buildVehicleImageURL(vehicleType, undefined, angle),
  } as AssetReportItem;
};

export const calculateTargetSocEstRange = (
  currentSoc: number,
  currentSocEstRange: number,
  targetSoc: number
) => (currentSocEstRange / currentSoc) * targetSoc;

const mapReadiness = (ready?: ReadyStatus) =>
  ready === ReadyStatus.READY
    ? true
    : ready === ReadyStatus.NOT_READY
      ? false
      : undefined;

/**
 * Maps lastCommunicated from API response to the standardized format
 * Handles both object format (old) and string format (new consolidated flag)
 * @param lastCommunicated - Can be an object with status/deviceTimestamp, a string (timestamp), or null/undefined
 * @returns Standardized lastCommunicated object with deviceTimestamp and status
 */
const mapLastCommunicated = (
  lastCommunicated?:
    | Partial<{
        status: LastCommunicated;
        deviceTimestamp: string;
      }>
    | string
    | null
): {
  deviceTimestamp: string | undefined;
  status: LastCommunicated | undefined;
} => {
  if (typeof lastCommunicated === 'string') {
    return {
      deviceTimestamp: lastCommunicated,
      status: undefined,
    };
  }
  if (typeof lastCommunicated === 'object' && lastCommunicated !== null) {
    return {
      deviceTimestamp: lastCommunicated.deviceTimestamp,
      status: lastCommunicated.status,
    };
  }
  return {
    deviceTimestamp: undefined,
    status: undefined,
  };
};
/**
 * The list sends prognostics as an array whose `type` is already the SAC type, but the details
 * endpoint keys them `dtc_SAC00N` and names the SAC separately. Anything that resolves to neither
 * is dropped: it would otherwise reach the cells as a diagnostic with no icon and a raw key label.
 */
export const toPrognosticList = (
  prognostics?: ApiReadinessItem['prognostics']
): ApiDiagnostic[] => {
  if (!prognostics) return [];
  // Both shapes drop what they cannot resolve: an unresolved entry has no icon or label, and
  // `collapseTwelveVoltBatteryDiagnostics` would not strip it when the flag is off.
  if (Array.isArray(prognostics)) {
    return prognostics.flatMap((entry) => {
      const type = resolveTwelveVoltSacType(entry.type);
      return type ? [{ ...entry, type } as ApiDiagnostic] : [];
    });
  }

  return Object.entries(prognostics).flatMap(([key, entry]) => {
    const type = resolveTwelveVoltSacType(key, entry.name);
    return type ? [{ ...entry, type } as ApiDiagnostic] : [];
  });
};

export const mapDiagnostics = (
  diagnostics?: ApiDiagnostic[],
  isTwelveVoltBatteryEnabled = false
): Diagnostic[] => {
  const excludedDiagnostics: string[] = [
    DiagnosticDTCType.BATTERY,
    DiagnosticDTCType.FUEL_LEVEL,
    DiagnosticDTCType.OIL_LIFE,
  ];
  const updatedDiagnostics = diagnostics?.filter(
    (diagnostic) => !excludedDiagnostics.includes(diagnostic.type)
  );

  return collapseTwelveVoltBatteryDiagnostics(
    updatedDiagnostics?.map((diagnostic) => ({
      ...diagnostic,
      originalAPIDiagnosticType: diagnostic.type,
    })),
    isTwelveVoltBatteryEnabled
  ) as Diagnostic[];
};

/**
 * The diagnostics API reports 12-volt battery prognostics as up to five SAC subtypes, of
 * which only the most severe critical one is surfaced. Non-critical subtypes are dropped,
 * because a non-critical 12-volt entry renders in neither column. When the feature is off
 * the subtypes are dropped, since there is no icon or label for them.
 *
 * A vehicle can report a low-battery DTC and prognostics at once, and both map to the same
 * `twelve_volt_battery` type. A critical DTC absorbs the prognostics so the pair renders as
 * one entry keeping the DTC's wording; a non-critical DTC renders in neither column, so the
 * prognostic stands on its own instead.
 */
export const collapseTwelveVoltBatteryDiagnostics = (
  diagnostics?: ApiDiagnostic[],
  isTwelveVoltBatteryEnabled = false
): ApiDiagnostic[] | undefined => {
  if (!diagnostics?.length) {
    return diagnostics;
  }

  const firstSubtypeIndex = diagnostics.findIndex((diagnostic) =>
    isTwelveVoltBatterySacType(diagnostic.type)
  );

  // Nothing to collapse: hand back the original array rather than a rebuilt copy.
  if (firstSubtypeIndex === -1) {
    return diagnostics;
  }

  const withoutSubtypes = () =>
    diagnostics.filter(
      (diagnostic) => !isTwelveVoltBatterySacType(diagnostic.type)
    );

  if (!isTwelveVoltBatteryEnabled) {
    return withoutSubtypes();
  }

  const hasCriticalLowBatteryDtc = diagnostics.some(
    ({ type, state }) =>
      type === DiagnosticDTCType.TWELVE_VOLT_BATTERY &&
      state === ReportDiagnosticState.CRITICAL
  );

  if (hasCriticalLowBatteryDtc) {
    return withoutSubtypes();
  }

  const subtypes = diagnostics.filter(
    ({ type, state }) =>
      isTwelveVoltBatterySacType(type) &&
      state === ReportDiagnosticState.CRITICAL
  );

  const mostSevere = pickMostSevereTwelveVoltSac(subtypes);

  if (!mostSevere) {
    // No critical subtype means nothing to render in either column.
    return withoutSubtypes();
  }

  const collapsed = {
    ...mostSevere,
    type: DiagnosticDTCType.TWELVE_VOLT_BATTERY,
  };

  // The collapsed entry takes the first subtype's slot, leaving every other
  // diagnostic in the position the API reported it.
  return diagnostics.flatMap((diagnostic, index) => {
    if (!isTwelveVoltBatterySacType(diagnostic.type)) {
      return diagnostic;
    }
    return index === firstSubtypeIndex ? collapsed : [];
  });
};

export type ApiAdvisorVehicleListItem = Entity & {
  type: { model: string; version: string };
  organization?: Partial<Entity>;
  hub?: Partial<Entity>;
  fleet?: Partial<Entity>;
  licensePlate?: string;
  vehicleType?: VehicleDetailsType;
  fan?: number;
  lastUpdated?: string;
  provisioning?: ProvisioningStatus | null;
  organizationPrimaryOwnershipStatus?: OrganizationOwnershipStatus | null;
  tcpsAccepted?: boolean | null;
  connectivity?: {
    onStar?: {
      connectionStatus?: OnstarConnectionStatus | null;
    };
  } | null;
  onstarSupportStatus?: OnstarSupportStatus | null;
  /** Boarding connection status from the new connectivity API field. */
  connectionStatus?: VehicleConnectionStatus | null;
};

export const mapApiAdvisorVehicleListItem = (
  {
    id,
    name,
    organization,
    fleet,
    vehicleType,
    fan,
    ...apiResponse
  }: Partial<ApiAdvisorVehicleListItem>,
  isNotEquippedFeatureFlag = false,
  isNewConnectivityStatusFeatureFlag = false
): AdvisorVehicleListItem => {
  const type = apiResponse?.type
    ? mapApiResponseToType(apiResponse?.type)
    : undefined;
  return {
    id,
    name,
    organization,
    fleet,
    fan,
    type,
    licensePlate: apiResponse.licensePlate ?? undefined,
    vehicleType,
    lastUpdated: apiResponse.lastUpdated,
    connectivityBoardingStatus: mapConnectivityStatusType({
      organizationOwnershipStatus:
        apiResponse?.organizationPrimaryOwnershipStatus,
      onStarConnectionStatus:
        apiResponse?.connectivity?.onStar?.connectionStatus,
      connectionStatus: apiResponse?.connectionStatus ?? undefined,
      onstarSupportStatus: apiResponse?.onstarSupportStatus,
      isNotEquippedFeatureFlag,
      isNewConnectivityStatusFeatureFlag,
    }),
  } as AdvisorVehicleListItem;
};
